import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, MapPin, Users, Award, Zap, CheckCircle2,
  Building2, TrendingUp, Clock, Globe, Lock, Target, Code, Shield
} from 'lucide-react';

const AIConsultingUSA = () => {
  const usaPresence = [
    {
      region: 'East Coast',
      cities: ['New York', 'Boston', 'Washington DC', 'Miami'],
      industries: 'Finance, Healthcare, Government',
      projects: '80+ projects'
    },
    {
      region: 'West Coast',
      cities: ['San Francisco', 'Los Angeles', 'Seattle', 'San Diego'],
      industries: 'Tech, Entertainment, E-commerce',
      projects: '60+ projects'
    },
    {
      region: 'Central US',
      cities: ['Chicago', 'Dallas', 'Austin', 'Denver'],
      industries: 'Manufacturing, Retail, Energy',
      projects: '40+ projects'
    },
    {
      region: 'Caribbean',
      cities: ['Puerto Rico', 'US Virgin Islands'],
      industries: 'Tourism, Finance, Healthcare',
      projects: '20+ projects'
    }
  ];

  const whyUSClients = [
    {
      icon: Clock,
      title: 'Fast Implementation',
      description: '3-8 week deployment vs 6-12 months with traditional US consulting firms',
      metric: '60-80% faster'
    },
    {
      icon: TrendingUp,
      title: 'Cost Effective',
      description: 'Senior AI expertise at 40-60% lower cost than Silicon Valley rates',
      metric: '40-60% savings'
    },
    {
      icon: Award,
      title: 'Proven Excellence',
      description: '99% client satisfaction across 200+ US and international projects',
      metric: '99% satisfaction'
    },
    {
      icon: Globe,
      title: 'US Business Hours',
      description: 'Dedicated US timezone overlap for real-time collaboration',
      metric: '4-6 hour overlap'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'SOC 2 Type II ready, HIPAA compliant, strict NDAs for US enterprises',
      metric: '100% compliant'
    },
    {
      icon: Code,
      title: 'Senior Engineers',
      description: '5+ years experience, many with US company backgrounds (Google, Amazon, Microsoft)',
      metric: 'Top 3% talent'
    }
  ];

  const usaIndustries = [
    {
      icon: Building2,
      name: 'Financial Services',
      clients: 'Banks, FinTech, Investment Firms',
      useCases: 'Fraud detection, algorithmic trading, risk assessment, robo-advisors',
      example: 'Deployed fraud detection AI for major US bank - 95% accuracy, $4M annual savings'
    },
    {
      icon: Shield,
      name: 'Healthcare',
      clients: 'Hospitals, Health Tech, Pharma',
      useCases: 'Diagnostic AI, patient monitoring, drug discovery, claims processing',
      example: 'HIPAA-compliant diagnostic AI for US hospital network - 95% accuracy in 6 weeks'
    },
    {
      icon: Code,
      name: 'Technology & SaaS',
      clients: 'Software Companies, Startups',
      useCases: 'AI product features, recommendation engines, NLP, computer vision',
      example: 'LLM-powered chatbot for SaaS company - 70% reduction in support tickets'
    },
    {
      icon: TrendingUp,
      name: 'Retail & E-commerce',
      clients: 'Retailers, E-commerce Platforms',
      useCases: 'Demand forecasting, personalization, inventory optimization, visual search',
      example: 'Recommendation engine for major retailer - 23% increase in conversion'
    },
    {
      icon: Users,
      name: 'Manufacturing',
      clients: 'Manufacturers, Industrial',
      useCases: 'Predictive maintenance, quality control, supply chain optimization',
      example: 'Computer vision for US manufacturer - 98% defect detection, $2M savings'
    },
    {
      icon: Globe,
      name: 'Professional Services',
      clients: 'Consulting, Legal, Accounting',
      useCases: 'Document analysis, automation, research assistants, data analytics',
      example: 'AI document analysis for law firm - 80% faster contract review'
    }
  ];

  const successMetrics = [
    {
      metric: '200+',
      label: 'US Projects Delivered',
      description: 'Across all 50 states and territories'
    },
    {
      metric: '99%',
      label: 'Client Satisfaction',
      description: 'Highest in industry'
    },
    {
      metric: '3-8',
      label: 'Weeks to Deploy',
      description: '60-80% faster than competitors'
    },
    {
      metric: '24/7',
      label: 'Support Available',
      description: 'US business hours + global coverage'
    }
  ];

  const services = [
    'AI Strategy & Consulting',
    'Machine Learning Development',
    'Generative AI Integration (GPT-4, Claude)',
    'Computer Vision Solutions',
    'Natural Language Processing',
    'Predictive Analytics',
    'AI Automation & RPA',
    'Data Engineering & Pipelines',
    'MLOps & Model Deployment',
    'AI Security & Compliance'
  ];

  return (
    <>
      <Helmet>
        <title>AI Consulting Company USA | AI Implementation Services United States | Agility</title>
        <meta
          name="description"
          content="Leading AI consulting company serving US clients. 200+ successful AI implementations across USA. 3-8 week deployment. HIPAA & SOC 2 compliant. Expert AI consultants for finance, healthcare, retail. 99% satisfaction."
        />
        <meta
          name="keywords"
          content="AI consulting company USA, AI consulting services United States, machine learning consulting USA, AI implementation USA, AI development company USA, AI consultants America, artificial intelligence consulting USA, ML consulting services USA"
        />
        <link rel="canonical" href="https://www.agilitytech.ai/ai-consulting-company-usa" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Consulting Company USA | AI Implementation Services United States" />
        <meta property="og:description" content="Leading AI consulting company serving US clients. 200+ successful implementations. 3-8 week deployment. HIPAA & SOC 2 compliant." />
        <meta property="og:url" content="https://www.agilitytech.ai/ai-consulting-company-usa" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />

        {/* Schema.org LocalBusiness markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Agility - AI Consulting Company USA",
            "description": "Leading AI consulting and implementation services for US businesses. 200+ successful projects across finance, healthcare, retail, manufacturing.",
            "url": "https://www.agilitytech.ai",
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "serviceType": "AI Consulting and Machine Learning Services",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.95",
              "reviewCount": "200",
              "bestRating": "5"
            },
            "offers": {
              "@type": "AggregateOffer",
              "availability": "https://schema.org/InStock",
              "priceCurrency": "USD"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "AI Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "AI Strategy Consulting",
                    "description": "Strategic AI planning and implementation guidance"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Machine Learning Development",
                    "description": "Custom ML models and AI solutions"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Generative AI Integration",
                    "description": "GPT-4, Claude, and LLM implementations"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-900 via-black to-teal-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(30deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6),linear-gradient(150deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6)]" style={{ backgroundSize: '80px 140px' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full mb-8">
            <MapPin className="w-4 h-4 text-blue-400" />
            <span className="text-blue-300 text-sm font-medium">Serving All 50 States + Caribbean</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Leading <span className="text-teal-400">AI Consulting Company</span> in USA
          </h1>

          <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
            Transform your US business with production-ready AI systems deployed in 3-8 weeks.
            200+ successful implementations across finance, healthcare, retail, and manufacturing.
            HIPAA & SOC 2 compliant. 99% client satisfaction.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Start Your AI Project
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies"
              className="group inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all"
            >
              View US Case Studies
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Success Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {successMetrics.map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-2">{item.metric}</div>
                <div className="text-white font-semibold mb-1">{item.label}</div>
                <div className="text-gray-400 text-sm">{item.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why US Clients Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why <span className="text-teal-600">US Companies</span> Choose Agility
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              World-class AI expertise with US business practices and compliance standards
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyUSClients.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="fade-in-up bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border-2 border-gray-200 hover:border-teal-500 transition-all">
                  <Icon className="w-12 h-12 text-teal-600 mb-6" />
                  <div className="text-3xl font-bold text-teal-600 mb-2">{item.metric}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* USA Presence */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-teal-600">Nationwide</span> AI Consulting Presence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Successfully delivered AI projects across all US regions and territories
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {usaPresence.map((region, index) => (
              <div key={index} className="fade-in-up bg-white rounded-xl p-8 border border-gray-200 hover:shadow-xl transition-all">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-6 h-6 text-teal-600" />
                  <h3 className="text-2xl font-bold text-gray-900">{region.region}</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-500 mb-2">MAJOR CITIES</div>
                    <div className="text-gray-700">{region.cities.join(', ')}</div>
                  </div>

                  <div>
                    <div className="text-sm font-semibold text-gray-500 mb-2">KEY INDUSTRIES</div>
                    <div className="text-gray-700">{region.industries}</div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-2xl font-bold text-teal-600">{region.projects}</div>
                    <div className="text-gray-600">Completed</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve in USA */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              AI Solutions for <span className="text-teal-600">US Industries</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Industry-specific AI implementations with US compliance and security standards
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {usaIndustries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div key={index} className="fade-in-up bg-gradient-to-br from-teal-50 to-white rounded-xl p-8 border border-teal-200">
                  <div className="flex items-start gap-4 mb-6">
                    <Icon className="w-10 h-10 text-teal-600 flex-shrink-0" />
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{industry.name}</h3>
                      <div className="text-teal-600 font-medium">{industry.clients}</div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="text-sm font-semibold text-gray-500 mb-2">USE CASES</div>
                    <p className="text-gray-700">{industry.useCases}</p>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-teal-200">
                    <div className="text-sm font-semibold text-teal-600 mb-2">SUCCESS STORY</div>
                    <p className="text-gray-700">{industry.example}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services for US Clients */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Comprehensive <span className="text-teal-400">AI Services</span> for US Enterprises
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              End-to-end AI solutions from strategy to deployment and maintenance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="fade-in-up flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:border-teal-500/50 transition-all">
                <CheckCircle2 className="w-6 h-6 text-teal-400 flex-shrink-0" />
                <span className="text-white font-medium">{service}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/ai-implementation-services"
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              View All Services
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your US Business with AI?
          </h2>
          <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
            Join 200+ US companies that trust Agility for their AI initiatives.
            Get a custom AI roadmap in 48 hours.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Schedule Free Consultation
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-6 text-teal-50">
            Call us at <span className="font-semibold">+91-7041402034</span> or email <span className="font-semibold">hello@agilitytech.ai</span>
          </p>
        </div>
      </section>
    </>
  );
};

export default AIConsultingUSA;
